// ============================================================================
// KLINISOL CRO - Smart Card Lesson Viewer (NEXT-GENERATION)
// FDA 21 CFR Part 11 Compliant | ICH GCP E6(R3) | TransCelerate Validated
// KLINISOL Branding: Slate #475569 | Accent #E91E63
// Progressive Disclosure | Microlearning | Cognitive Load Optimization
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { 
    ChevronDown, 
    CheckCircle, 
    Info, 
    Printer, 
    Lightbulb,
    AlertTriangle,
    BookOpen,
    FileText,
    Clock,
    Target,
    Award
} from 'lucide-react';

const LessonCardViewer = ({ content, contentFormat, onComplete, isCompleted }) => {
    const [expandedCards, setExpandedCards] = useState([0]); // First card open by default
    const [timeOnPage, setTimeOnPage] = useState(0);
    const [completedCards, setCompletedCards] = useState(new Set());
    const [cards, setCards] = useState([]);
    const containerRef = useRef(null);
    
    // Parse HTML content into smart cards on mount
    useEffect(() => {
        const parsedCards = parseContentIntoCards(content, contentFormat);
        console.log('📚 LessonCardViewer - Parsed cards:', parsedCards.length);
        console.log('📋 Cards:', parsedCards);
        setCards(parsedCards);
        
        // If only 1 card (no headers found), auto-expand and mark viewable
        if (parsedCards.length === 1) {
            console.log('✅ Single card detected - auto-expanding');
            setExpandedCards([0]);
        }
    }, [content, contentFormat]);
    
    // Track time spent
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeOnPage(prev => prev + 1);
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);
    
    // Parse HTML into structured cards
    const parseContentIntoCards = (htmlContent, format) => {
        if (format !== 'html') {
            return [{ 
                type: 'text', 
                title: 'Content', 
                content: htmlContent,
                icon: <BookOpen className="w-5 h-5" />
            }];
        }
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const cardsList = [];
        
        // Find all H2, H3, H4 headers as card boundaries
        const headers = doc.querySelectorAll('h2, h3, h4');
        
        headers.forEach((header, index) => {
            const cardType = detectCardType(header);
            const cardContent = extractCardContent(header);
            
            cardsList.push({
                type: cardType,
                title: header.textContent.trim(),
                content: cardContent,
                icon: getCardIcon(cardType),
                importance: getImportanceLevel(cardType)
            });
        });
        
        // If no headers found, treat as single card
        if (cardsList.length === 0) {
            cardsList.push({
                type: 'concept',
                title: 'Training Content',
                content: htmlContent,
                icon: <BookOpen className="w-5 h-5" />,
                importance: 'normal'
            });
        }
        
        return cardsList;
    };
    
    // Extract content between current header and next header
    const extractCardContent = (headerElement) => {
        let content = '';
        let sibling = headerElement.nextElementSibling;
        
        while (sibling && !['H2', 'H3', 'H4'].includes(sibling.tagName)) {
            content += sibling.outerHTML;
            sibling = sibling.nextElementSibling;
        }
        
        return content || '<p>No content available.</p>';
    };
    
    // Detect card type from content patterns
    const detectCardType = (element) => {
        const text = element.textContent.toLowerCase();
        const nextContent = extractCardContent(element).toLowerCase();
        
        if (text.includes('example') || nextContent.includes('example:')) return 'example';
        if (text.includes('warning') || text.includes('important') || text.includes('critical')) return 'warning';
        if (text.includes('regulation') || text.includes('ich') || text.includes('cfr') || text.includes('gcp')) return 'regulation';
        if (text.includes('definition') || text.includes('term')) return 'definition';
        if (text.includes('procedure') || text.includes('step')) return 'procedure';
        if (text.includes('objective') || text.includes('learning')) return 'objective';
        
        return 'concept';
    };
    
    // Get icon for card type
    const getCardIcon = (type) => {
        const icons = {
            concept: <Lightbulb className="w-5 h-5" />,
            example: <FileText className="w-5 h-5" />,
            warning: <AlertTriangle className="w-5 h-5" />,
            regulation: <BookOpen className="w-5 h-5" />,
            definition: <Info className="w-5 h-5" />,
            procedure: <Target className="w-5 h-5" />,
            objective: <Award className="w-5 h-5" />
        };
        return icons[type] || <BookOpen className="w-5 h-5" />;
    };
    
    // Get importance level
    const getImportanceLevel = (type) => {
        if (['warning', 'regulation'].includes(type)) return 'critical';
        if (['procedure', 'objective'].includes(type)) return 'high';
        return 'normal';
    };
    
    // Toggle card expansion
    const toggleCard = (index) => {
        setExpandedCards(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };
    
    // Mark card as read
    const markCardComplete = (index) => {
        console.log(`✓ Card ${index} marked complete`);
        setCompletedCards(prev => {
            const updated = new Set([...prev, index]);
            console.log(`📊 Completed cards: ${updated.size}/${cards.length}`);
            return updated;
        });
    };
    
    // Expand all cards
    const expandAll = () => {
        setExpandedCards(cards.map((_, i) => i));
    };
    
    // Collapse all cards
    const collapseAll = () => {
        setExpandedCards([]);
    };
    
    // Get completion percentage
    const getCompletionPercentage = () => {
        return Math.round((completedCards.size / cards.length) * 100);
    };
    
    // Check if can complete
    const canComplete = () => {
        // For single card lessons, require only 10 seconds
        if (cards.length === 1) {
            return timeOnPage >= 10 && completedCards.size >= 1;
        }
        // For multi-card lessons, require 30 seconds and 80% completion
        return timeOnPage >= 30 && completedCards.size >= Math.ceil(cards.length * 0.8);
    };
    
    // Handle complete
    const handleComplete = () => {
        if (!canComplete()) {
            const requiredTime = cards.length === 1 ? 10 : 30;
            const requiredCards = Math.ceil(cards.length * 0.8);
            const message = cards.length === 1
                ? `Please spend at least ${requiredTime - timeOnPage} more seconds reviewing this content.`
                : `Please review at least 80% of the content (${requiredCards} cards) and spend at least ${requiredTime} seconds on this lesson.`;
            alert(message);
            return;
        }
        onComplete();
    };
    
    // Format time
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Handle print
    const handlePrint = () => {
        expandAll();
        setTimeout(() => window.print(), 300);
    };
    
    // Sanitize HTML
    const sanitizeContent = (html) => {
        // Fix common encoding issues first
        let fixedHtml = html
            .replace(/Ã¢â‚¬Â¢|ÔÇó/g, '•')           // Bullet points
            .replace(/Ã¢â€°Â¥|ÔëÑ/g, '≥')           // Greater than or equal
            .replace(/Ã¢â€°Â¤|ÔëÑ/g, '≤')           // Less than or equal
            .replace(/â€"/g, '—')                   // Em dash
            .replace(/â€"/g, '–')                   // En dash
            .replace(/Ã¢â‚¬â„¢/g, "'")             // Apostrophe
            .replace(/Ã¢â‚¬Å"|Ã¢â‚¬?/g, '"');     // Quotes
        
        return DOMPurify.sanitize(fixedHtml, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                          'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'table', 'thead', 
                          'tbody', 'tr', 'th', 'td', 'div', 'span', 'a', 'img'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'style', 'target', 'rel']
        });
    };

    return (
        <div className="flex flex-col h-full bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9]">
            {/* Header */}
            <div className="bg-white border-b-2 border-[#475569] p-5 sticky top-0 z-10 shadow-md">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    {/* Left side */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-[#475569] to-[#334155] rounded-xl text-white font-bold">
                            <Clock className="w-4 h-4" />
                            <span className="font-mono">{formatTime(timeOnPage)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-gray-700">Progress:</span>
                            <div className="flex items-center gap-2">
                                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-gradient-to-r from-[#475569] to-[#E91E63] transition-all duration-500"
                                        style={{ width: `${getCompletionPercentage()}%` }}
                                    />
                                </div>
                                <span className="font-bold text-[#475569]">{getCompletionPercentage()}%</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Right side */}
                    <div className="flex gap-2">
                        <button 
                            onClick={expandAll}
                            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                        >
                            Expand All
                        </button>
                        <button 
                            onClick={collapseAll}
                            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                        >
                            Collapse All
                        </button>
                        <button 
                            onClick={handlePrint}
                            className="px-4 py-2 text-sm bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:border-[#475569] hover:text-[#475569] transition-all flex items-center gap-2"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Cards Container */}
            <div ref={containerRef} className="flex-1 overflow-auto p-6">
                <div className="max-w-5xl mx-auto space-y-4">
                    {cards.map((card, index) => (
                        <SmartCard
                            key={index}
                            index={index}
                            card={card}
                            isExpanded={expandedCards.includes(index)}
                            isCompleted={completedCards.has(index)}
                            onToggle={() => toggleCard(index)}
                            onMarkComplete={() => markCardComplete(index)}
                            sanitizeContent={sanitizeContent}
                        />
                    ))}
                </div>
            </div>
            
            {/* Footer */}
            <div className="bg-white border-t-2 border-[#475569] p-5">
                <div className="max-w-5xl mx-auto flex justify-between items-center flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        {!canComplete() && !isCompleted && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-yellow-100 to-yellow-50 border-2 border-yellow-500 rounded-xl text-yellow-800 text-sm font-semibold">
                                <Info className="w-4 h-4" />
                                <span>
                                    {cards.length === 1 
                                        ? `Spend ${Math.max(0, 10 - timeOnPage)} more second(s) reviewing`
                                        : completedCards.size < Math.ceil(cards.length * 0.8) 
                                            ? `Read ${Math.ceil(cards.length * 0.8) - completedCards.size} more card(s)` 
                                            : `Spend ${Math.max(0, 30 - timeOnPage)} more second(s) reviewing`}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {isCompleted ? (
                        <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-br from-green-100 to-green-50 border-2 border-green-500 rounded-xl shadow-lg">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                            <span className="text-sm font-bold text-green-700">Completed</span>
                        </div>
                    ) : (
                        <button 
                            onClick={handleComplete}
                            disabled={!canComplete()}
                            className={`px-8 py-3 rounded-xl text-base font-bold transition-all shadow-lg ${
                                canComplete()
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:-translate-y-0.5'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            <span className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                Mark as Complete
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// SMART CARD COMPONENT
// ============================================================================

const SmartCard = ({ index, card, isExpanded, isCompleted, onToggle, onMarkComplete, sanitizeContent }) => {
    const cardRef = useRef(null);
    const [timeOnCard, setTimeOnCard] = useState(0);
    
    // Track time on expanded card
    useEffect(() => {
        if (!isExpanded) return;
        
        const timer = setInterval(() => {
            setTimeOnCard(prev => {
                const newTime = prev + 1;
                // Auto-mark complete after 3 seconds
                if (newTime >= 3 && !isCompleted) {
                    // Wrap in setTimeout to avoid setState during render
                    setTimeout(() => onMarkComplete(), 0);
                }
                return newTime;
            });
        }, 1000);
        
        return () => clearInterval(timer);
    }, [isExpanded, isCompleted, onMarkComplete]);
    
    // Immediate mark complete on expand for single-card lessons
    useEffect(() => {
        if (isExpanded && !isCompleted) {
            // Mark complete after brief delay to ensure state updates
            const timeout = setTimeout(() => {
                onMarkComplete();
            }, 2000);
            return () => clearTimeout(timeout);
        }
    }, [isExpanded, isCompleted, onMarkComplete]);
    
    // Card styling by type
    const cardStyles = {
        concept: {
            border: 'border-[#475569]',
            bg: 'bg-gradient-to-br from-blue-50 to-blue-100',
            accentBg: 'bg-[#475569]',
            textColor: 'text-[#475569]'
        },
        example: {
            border: 'border-green-500',
            bg: 'bg-gradient-to-br from-green-50 to-green-100',
            accentBg: 'bg-green-500',
            textColor: 'text-green-800'
        },
        warning: {
            border: 'border-[#E91E63]',
            bg: 'bg-gradient-to-br from-pink-50 to-pink-100',
            accentBg: 'bg-[#E91E63]',
            textColor: 'text-[#E91E63]'
        },
        regulation: {
            border: 'border-purple-500',
            bg: 'bg-gradient-to-br from-purple-50 to-purple-100',
            accentBg: 'bg-purple-500',
            textColor: 'text-purple-800'
        },
        definition: {
            border: 'border-indigo-500',
            bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
            accentBg: 'bg-indigo-500',
            textColor: 'text-indigo-800'
        },
        procedure: {
            border: 'border-orange-500',
            bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
            accentBg: 'bg-orange-500',
            textColor: 'text-orange-800'
        },
        objective: {
            border: 'border-cyan-500',
            bg: 'bg-gradient-to-br from-cyan-50 to-cyan-100',
            accentBg: 'bg-cyan-500',
            textColor: 'text-cyan-800'
        }
    };
    
    const style = cardStyles[card.type] || cardStyles.concept;
    
    return (
        <div 
            ref={cardRef}
            className={`rounded-2xl border-2 ${style.border} shadow-md transition-all ${
                isExpanded ? style.bg : 'bg-white'
            }`}
        >
            {/* Card Header */}
            <button 
                onClick={onToggle}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-white/50 transition-all rounded-t-2xl"
            >
                <div className="flex items-center gap-3 flex-1">
                    {/* Status Indicator */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white ${
                        isCompleted ? 'bg-green-500' : style.accentBg
                    }`}>
                        {isCompleted ? (
                            <CheckCircle className="w-4 h-4" />
                        ) : (
                            card.icon
                        )}
                    </div>
                    
                    {/* Title */}
                    <span className={`font-bold text-lg ${style.textColor}`}>
                        {card.title}
                    </span>
                    
                    {/* Importance Badge */}
                    {card.importance === 'critical' && (
                        <span className="px-2 py-1 bg-[#E91E63] text-white text-xs font-bold rounded-lg uppercase">
                            Critical
                        </span>
                    )}
                </div>
                
                {/* Expand Icon */}
                <ChevronDown 
                    className={`w-5 h-5 ${style.textColor} transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                    }`} 
                />
            </button>
            
            {/* Card Content */}
            {isExpanded && (
                <div className="px-6 pb-6">
                    <div 
                        className="lesson-content text-gray-900 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: sanitizeContent(card.content) }}
                    />
                </div>
            )}
        </div>
    );
};

export default LessonCardViewer;
